import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Page from "../Components/Page";
import DashBoardFilterPane from "../Components/Dashboard/DashBoardFilterPane";
import DashboardTable from "../Components/Dashboard/DashboardTable";
import DashboardMapView from "../Components/Dashboard/DashboardMapView";
import {
  fetchDashboardSummary,
} from "../Components/axiosFetchers";
import { useAuth } from "../Components/Auth/useAuth";
import { useClientConfig } from "../hooks/useClientConfig";
import AggregatorSelectedGrowerPanel, {
  AggregatorScopeWarning,
  useSelectedAggregatorGrower,
} from "../Components/AggregatorSelectedGrowerPanel.jsx";
import {
  DATA_READ_SCOPE,
  DATA_WRITE_SCOPE,
  selectedGrowerHasScope,
} from "../portal/aggregatorGrowerContext.js";
import { formatAcres } from "../utils/acreageFormatting.js";

function capitalize(inputString) {
  return inputString
    .split("_")
    .map((token) => token.charAt(0).toUpperCase() + token.slice(1).toLowerCase())
    .join(" ");
}

function normalizeCropKey(crop) {
  if (crop === null || crop === undefined) {
    return "";
  }

  return String(crop).trim().toLowerCase();
}

function isUnknownCrop(crop) {
  const normalized = normalizeCropKey(crop);
  return normalized === "" || normalized === "unknown" || normalized === "null";
}

function buildCropYearKey(cropYear, crop) {
  const normalizedYear =
    cropYear === null || cropYear === undefined || cropYear === "?" ? "Draft" : String(cropYear);

  return `${normalizedYear}::${normalizeCropKey(crop)}`;
}

function buildCropYearLabel(cropYear, crop) {
  return `${cropYear ?? "Draft"} • ${
    isUnknownCrop(crop) ? "Unassigned Crop" : capitalize(String(crop))
  }`;
}

function calculateCentroid(boundaries = []) {
  if (!Array.isArray(boundaries) || boundaries.length === 0) {
    return null;
  }

  const totals = boundaries.reduce(
    (acc, point) => ({
      lat: acc.lat + point.lat,
      lng: acc.lng + point.lng,
    }),
    { lat: 0, lng: 0 }
  );

  return {
    lat: totals.lat / boundaries.length,
    lng: totals.lng / boundaries.length,
  };
}

function isFieldMappable(field) {
  return (
    (Array.isArray(field.boundaries) && field.boundaries.length > 2) ||
    Boolean(field.centroid)
  );
}

function toArray(value) {
  if (Array.isArray(value?.data)) {
    return value.data;
  }

  return Array.isArray(value) ? value : [];
}

function readValue(record, keys, fallback = null) {
  for (const key of keys) {
    if (record?.[key] !== undefined && record?.[key] !== null) {
      return record[key];
    }
  }

  return fallback;
}

function normalizeBoundaryPoint(point) {
  return {
    ...point,
    lat: Number(readValue(point, ["lat", "latitude"], 0)),
    lng: Number(readValue(point, ["lng", "lon", "longitude"], 0)),
  };
}

function normalizeTableRow(row) {
  const farmRecord = readValue(row, ["farm"], null);
  const fieldRecord = readValue(row, ["field"], null);
  const cropyearRecord = readValue(row, ["cropyear"], null);
  const cropYear = readValue(row, ["crop_year", "cropYear"], null);
  const crop = readValue(row, ["crop", "crop_this_year", "cropThisYear"], null);

  return {
    farmId: String(readValue(farmRecord, ["id"], readValue(row, ["farm_id", "farmId"], ""))),
    farmPublicId: readValue(
      farmRecord,
      ["public_id", "publicId"],
      readValue(row, ["farm_public_id", "farmPublicId"], "")
    ),
    fieldId: String(
      readValue(fieldRecord, ["id"], readValue(row, ["field_id", "fieldId"], ""))
    ),
    fieldPublicId: readValue(
      fieldRecord,
      ["public_id", "publicId"],
      readValue(row, ["field_public_id", "fieldPublicId"], "")
    ),
    cropyearId: String(
      readValue(
        cropyearRecord,
        ["id"],
        readValue(row, ["cropyear_id", "cropyearId", "id"], "")
      )
    ),
    cropyearPublicId: readValue(
      cropyearRecord,
      ["public_id", "publicId"],
      readValue(row, ["cropyear_public_id", "cropyearPublicId"], "")
    ),
    analysisId: readValue(row, ["analysis_id", "analysisId"], false) || false,
    farmName: readValue(farmRecord, ["name"], readValue(row, ["farm_name", "farmName"], "")),
    fieldName: readValue(
      fieldRecord,
      ["name"],
      readValue(row, ["field_name", "fieldName"], "")
    ),
    fieldSize: readValue(
      fieldRecord,
      ["size", "field_size", "fieldSize"],
      readValue(row, ["field_size", "fieldSize"], "")
    ),
    cropYear,
    crop,
    workflow_state: readValue(row, ["workflow_state"], ""),
    data_status: readValue(row, ["data_status"], ""),
    can_run_analysis: Boolean(readValue(row, ["can_run_analysis", "canRunAnalysis"], false)),
    analysis_readiness: readValue(row, ["analysis_readiness"], null),
    cropYearCropKey: buildCropYearKey(cropYear, crop),
    cropYearCropLabel: buildCropYearLabel(cropYear, crop),
  };
}

function normalizeMapField(field) {
  const farmRecord = readValue(field, ["farm"], null);
  const fieldId = readValue(field, ["id", "field_id", "fieldId"], "");
  const boundaries = toArray(readValue(field, ["boundaries"], [])).map(normalizeBoundaryPoint);
  const rawCropYears = toArray(
    readValue(field, ["crop_years", "cropYears"], [])
  );
  const normalizedCropYears = rawCropYears.map((cropYear) => {
    const normalized = normalizeTableRow({
      farm: readValue(cropYear, ["farm"], farmRecord),
      field: readValue(cropYear, ["field"], {
        id: readValue(cropYear, ["field_id", "fieldId"], fieldId),
        public_id: readValue(
          cropYear,
          ["field_public_id", "fieldPublicId"],
          readValue(field, ["public_id", "publicId"], "")
        ),
        name: readValue(cropYear, ["field_name", "fieldName"], readValue(field, ["field_name", "fieldName"], "")),
        size: readValue(cropYear, ["field_size", "fieldSize"], readValue(field, ["field_size", "fieldSize"], "")),
      }),
      cropyear: readValue(cropYear, ["cropyear"], cropYear),
      ...cropYear,
    });

    return normalized;
  });

  const centroidRecord = readValue(field, ["centroid"], null);

  return {
    farmId: String(readValue(farmRecord, ["id"], readValue(field, ["farm_id", "farmId"], ""))),
    farmPublicId: readValue(
      farmRecord,
      ["public_id", "publicId"],
      readValue(field, ["farm_public_id", "farmPublicId"], "")
    ),
    farmName: readValue(farmRecord, ["name"], readValue(field, ["farm_name", "farmName"], "")),
    province: readValue(farmRecord, ["province"], readValue(field, ["province"], null)),
    fieldId: String(fieldId),
    fieldPublicId: readValue(field, ["public_id", "field_public_id", "fieldPublicId"], ""),
    fieldName: readValue(field, ["field_name", "fieldName", "name"], ""),
    fieldSize: readValue(field, ["field_size", "fieldSize"], ""),
    fieldAddress: readValue(field, ["field_address", "fieldAddress"], ""),
    fieldBoundariesSize: readValue(
      field,
      ["field_boundaries_size", "fieldBoundariesSize"],
      ""
    ),
    hasBoundaries: Boolean(
      readValue(field, ["has_boundaries", "hasBoundaries"], boundaries.length > 2)
    ),
    boundaries,
    centroid: centroidRecord
      ? normalizeBoundaryPoint(centroidRecord)
      : calculateCentroid(boundaries),
    cropYears: normalizedCropYears,
  };
}

function buildQuickFacts(quickFacts = {}) {
  const crops = toArray(readValue(quickFacts, ["crops"], []))
    .map((crop) => capitalize(String(crop)))
    .join(", ");
  const yearRange = readValue(quickFacts, ["year_range", "yearRange"], null);
  const yearLabel =
    readValue(yearRange, ["label"], null) ||
    (yearRange &&
    readValue(yearRange, ["min"], null) !== null &&
    readValue(yearRange, ["max"], null) !== null
      ? `${readValue(yearRange, ["min"])} - ${readValue(yearRange, ["max"])}`
      : null);

  return [
    {
      label: "Acres",
      value: formatAcres(
        readValue(quickFacts, ["total_acres", "totalAcres"], 0)
      ),
    },
    { label: "Crops", value: crops || "No crops yet" },
    {
      label: "Total Fields",
      value: readValue(quickFacts, ["total_fields", "totalFields"], 0),
    },
    {
      label: "Total Farms",
      value: readValue(quickFacts, ["total_farms", "totalFarms"], 0),
    },
    { label: "Years", value: yearLabel || "Drafts only" },
  ];
}

function normalizeFilterOptions(filters = {}) {
  const farms = toArray(readValue(filters, ["farms"], [])).map((farm) => ({
    value: String(readValue(farm, ["id", "value"], "")),
    publicId: readValue(farm, ["public_id", "publicId", "value"], ""),
    label: readValue(farm, ["label", "name"], ""),
  }));

  const fields = toArray(readValue(filters, ["fields"], [])).map((field) => ({
    value: String(readValue(field, ["id", "value"], "")),
    publicId: readValue(field, ["public_id", "publicId", "value"], ""),
    label: readValue(field, ["label", "name"], ""),
    farmId: String(farms.find((farm) =>
      farm.publicId === readValue(field, ["farm_id", "farmId"], "")
    )?.value || readValue(readValue(field, ["farm"], null), ["id"],
      readValue(field, ["farm_id", "farmId"], ""))),
  }));

  const cropYears = toArray(readValue(filters, ["crop_years", "cropYears"], []))
    .map((cropYear) => {
      const rawValue = String(readValue(cropYear, ["value"], ""));
      const [rawYear = "", rawCrop = ""] = rawValue.split("::");
      const normalizedYear = rawYear.trim();
      const normalizedCrop = rawCrop.trim();
      const year =
        normalizedYear !== "" && normalizedYear !== "?" ? normalizedYear : readValue(cropYear, ["year", "crop_year", "cropYear"], null);
      const crop =
        normalizedCrop !== "" ? normalizedCrop : readValue(cropYear, ["crop"], null);

      if (year === null || year === undefined || year === "?" || isUnknownCrop(crop)) {
        return null;
      }

      return {
        value: buildCropYearKey(year, crop),
        label: buildCropYearLabel(year, crop),
      };
    })
    .filter(Boolean);

  return {
    farms,
    fields,
    cropYears,
  };
}

export default function DashBoardPage({ requireSelectedGrower = false }) {
  const { user } = useAuth();
  const selectedAggregatorGrower = useSelectedAggregatorGrower();
  const selectedAggregatorGrowerPublicId =
    selectedAggregatorGrower?.grower_profile_public_id || "";
  const canReadSelectedGrower =
    !requireSelectedGrower ||
    selectedGrowerHasScope(selectedAggregatorGrower, DATA_READ_SCOPE);
  const canWriteSelectedGrower =
    !requireSelectedGrower ||
    selectedGrowerHasScope(selectedAggregatorGrower, DATA_WRITE_SCOPE);
  const navigate = useNavigate();
  const { config } = useClientConfig();
  const [tableData, setTableData] = useState([]);
  const [fieldData, setFieldData] = useState([]);
  const [summaryData, setSummaryData] = useState(false);
  const [dashboardFilters, setDashboardFilters] = useState({
    farms: [],
    fields: [],
    cropYears: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [activeView, setActiveView] = useState("table");
  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [activeFilters, setActiveFilters] = useState({
    farmId: "",
    fieldId: "",
    cropYearCropKey: "",
  });

  useEffect(() => {
    let current = true;
    if (
      requireSelectedGrower &&
      (!selectedAggregatorGrowerPublicId || !canReadSelectedGrower)
    ) {
      setIsLoading(false);
      setSummaryData(false);
      setDashboardFilters({
        farms: [],
        fields: [],
        cropYears: [],
      });
      setTableData([]);
      setFieldData([]);
      return;
    }

    async function getDashboardData() {
      setIsLoading(true);
      setLoadError("");

      try {
        const summary = await fetchDashboardSummary(user);
        if (!current) return;
        const quickFacts = readValue(summary, ["quick_facts", "quickFacts"], {});
        const filters = readValue(summary, ["filters"], {});
        const nextTableData = toArray(
          readValue(summary, ["table_rows", "tableRows"], [])
        ).map(normalizeTableRow);
        const nextFieldData = toArray(
          readValue(summary, ["map_fields", "mapFields"], [])
        ).map(normalizeMapField);

        setSummaryData(buildQuickFacts(quickFacts));
        setDashboardFilters(normalizeFilterOptions(filters));
        setTableData(nextTableData);
        setFieldData(nextFieldData);
      } catch (error) {
        if (!current) return;
        console.error("Failed to load dashboard summary", error);
        setLoadError("We couldn't load your farms and fields. Please retry.");
        setSummaryData(false);
        setDashboardFilters({
          farms: [],
          fields: [],
          cropYears: [],
        });
        setTableData([]);
        setFieldData([]);
      } finally {
        if (current) setIsLoading(false);
      }
    }

    getDashboardData();
    return () => { current = false; };
  }, [
    canReadSelectedGrower,
    requireSelectedGrower,
    selectedAggregatorGrowerPublicId,
    user,
    reloadKey,
  ]);

  const filteredTableData = useMemo(() => {
    return tableData.filter((row) => {
      if (activeFilters.farmId !== "" && String(row.farmId) !== activeFilters.farmId) {
        return false;
      }

      if (activeFilters.fieldId !== "" && String(row.fieldId) !== activeFilters.fieldId) {
        return false;
      }

      if (
        activeFilters.cropYearCropKey !== "" &&
        row.cropYearCropKey !== activeFilters.cropYearCropKey
      ) {
        return false;
      }

      return true;
    });
  }, [activeFilters, tableData]);

  const filteredFieldData = useMemo(() => {
    return fieldData.filter((field) => {
      if (activeFilters.farmId !== "" && String(field.farmId) !== activeFilters.farmId) {
        return false;
      }

      if (activeFilters.fieldId !== "" && String(field.fieldId) !== activeFilters.fieldId) {
        return false;
      }

      if (activeFilters.cropYearCropKey !== "") {
        const matchesCropYear = field.cropYears.some(
          (cropYear) =>
            cropYear.cropYearCropKey === activeFilters.cropYearCropKey
        );

        if (!matchesCropYear) {
          return false;
        }
      }

      return true;
    });
  }, [activeFilters, fieldData]);

  const filteredMapFieldData = useMemo(
    () => filteredFieldData.filter(isFieldMappable),
    [filteredFieldData]
  );

  const farmOptions = useMemo(() => {
    if (dashboardFilters.farms.length > 0) {
      return dashboardFilters.farms;
    }

    return fieldData
      .map((field) => ({
        value: String(field.farmId),
        label: field.farmName,
      }))
      .filter(
        (option, index, options) =>
          options.findIndex((candidate) => candidate.value === option.value) ===
          index
      )
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [dashboardFilters.farms, fieldData]);

  const availableFields = useMemo(() => {
    if (dashboardFilters.fields.length > 0) {
      if (activeFilters.farmId === "") {
        return dashboardFilters.fields;
      }

      return dashboardFilters.fields.filter(
        (field) => String(field.farmId) === activeFilters.farmId
      );
    }

    if (activeFilters.farmId === "") {
      return fieldData;
    }

    return fieldData.filter(
      (field) => String(field.farmId) === activeFilters.farmId
    );
  }, [activeFilters.farmId, dashboardFilters.fields, fieldData]);

  const fieldOptions = useMemo(() => {
    return availableFields
      .map((field) => ({
        value: String(field.fieldId || field.value),
        label:
          activeFilters.farmId === ""
            ? field.farmName
              ? `${field.fieldName} (${field.farmName})`
              : field.label
            : field.fieldName || field.label,
      }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [activeFilters.farmId, availableFields]);

  const availableCropYears = useMemo(() => {
    if (dashboardFilters.cropYears.length > 0) {
      return dashboardFilters.cropYears.map((cropYear) => ({
        cropYearCropKey: cropYear.value,
        cropYearCropLabel: cropYear.label,
      }));
    }

    const scopedFields =
      activeFilters.fieldId !== ""
        ? availableFields.filter(
            (field) => String(field.fieldId || field.value) === activeFilters.fieldId
          )
        : availableFields;

    return scopedFields.flatMap((field) =>
      (field.cropYears || []).map((cropYear) => ({
        ...cropYear,
        farmId: field.farmId,
        fieldId: field.fieldId || field.value,
      }))
    );
  }, [activeFilters.fieldId, availableFields, dashboardFilters.cropYears]);

  const cropYearOptions = useMemo(() => {
    return availableCropYears
      .map((cropYear) => ({
        value: cropYear.cropYearCropKey,
        label: cropYear.cropYearCropLabel,
      }))
      .filter(
        (option, index, options) =>
          options.findIndex((candidate) => candidate.value === option.value) ===
          index
      )
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [availableCropYears]);

  const handleFarmChange = (farmId) => {
    setActiveFilters({
      farmId,
      fieldId: "",
      cropYearCropKey: "",
    });
  };

  const handleFieldChange = (fieldId) => {
    if (fieldId === "") {
      setActiveFilters((currentFilters) => ({
        ...currentFilters,
        fieldId: "",
        cropYearCropKey: "",
      }));
      return;
    }

    const selectedField = availableFields.find(
      (field) => String(field.fieldId || field.value) === fieldId
    );

    setActiveFilters({
      farmId: selectedField ? String(selectedField.farmId || "") : "",
      fieldId,
      cropYearCropKey: "",
    });
  };

  const handleCropYearChange = (cropYearCropKey) => {
    if (cropYearCropKey === "") {
      setActiveFilters((currentFilters) => ({
        ...currentFilters,
        cropYearCropKey: "",
      }));
      return;
    }

    setActiveFilters((currentFilters) => {
      if (fieldData.length === 0) {
        return {
          ...currentFilters,
          cropYearCropKey,
        };
      }

      const scopedFields =
        currentFilters.fieldId !== ""
          ? availableFields.filter(
              (field) => String(field.fieldId || field.value) === currentFilters.fieldId
            )
          : availableFields;

      const matchingFields = scopedFields.filter((field) =>
        (field.cropYears || []).some(
          (cropYear) => cropYear.cropYearCropKey === cropYearCropKey
        )
      );

      if (matchingFields.length === 1) {
        return {
          farmId: String(matchingFields[0].farmId || ""),
          fieldId: String(matchingFields[0].fieldId || matchingFields[0].value),
          cropYearCropKey,
        };
      }

      return {
        ...currentFilters,
        cropYearCropKey,
      };
    });
  };

  const handleClearFilters = () => {
    setActiveFilters({
      farmId: "",
      fieldId: "",
      cropYearCropKey: "",
    });
  };

  useEffect(() => {
    if (filteredMapFieldData.length === 0) {
      setSelectedFieldId(null);
      return;
    }

    const selectedFieldStillVisible = filteredMapFieldData.some(
      (field) => field.fieldId === selectedFieldId
    );

    if (!selectedFieldStillVisible) {
      setSelectedFieldId(filteredMapFieldData[0].fieldId);
    }
  }, [filteredMapFieldData, selectedFieldId]);

  const handleOpenSelectedField = () => {
    const selectedField = filteredFieldData.find(
      (field) => field.fieldId === selectedFieldId
    );

    if (!selectedField) {
      return;
    }

    navigate(`/farm/${selectedField.farmId}/field/${selectedField.fieldId}`);
  };

  const handleSelectRelativeField = (direction) => {
    if (filteredMapFieldData.length === 0) {
      return;
    }

    const currentIndex = filteredMapFieldData.findIndex(
      (field) => field.fieldId === selectedFieldId
    );
    const safeIndex = currentIndex < 0 ? 0 : currentIndex;
    const nextIndex =
      (safeIndex + direction + filteredMapFieldData.length) %
      filteredMapFieldData.length;

    setSelectedFieldId(filteredMapFieldData[nextIndex].fieldId);
  };

  document.title = "Dashboard Page - Field To Market Canada";

  return (
    <Page
      title={"Dashboard"}
      headerBorderColor={"border-[#00adee]"}
      showQuickFacts={false}
      dashboardData={summaryData}
      quickFactsVariant="inline-card"
      headerVariant="hybrid"
      contentWidth="wide"
      padding="px-4 pb-10 pt-5 md:px-6 md:pb-12"
      introContent={null}
    >
      <div className="h-full w-full text-[#666666]">
        {requireSelectedGrower ? (
          <div className="mb-5">
            <AggregatorSelectedGrowerPanel
              selectedGrower={selectedAggregatorGrower}
            />
          </div>
        ) : null}
        {requireSelectedGrower &&
        selectedAggregatorGrowerPublicId &&
        !canReadSelectedGrower ? (
          <AggregatorScopeWarning
            message="This selected grower link cannot view dashboard or field records."
            requiredScope={DATA_READ_SCOPE}
          />
        ) : null}
        {requireSelectedGrower &&
        (!selectedAggregatorGrowerPublicId || !canReadSelectedGrower) ? null : (
        <div className="w-full">
          {Array.isArray(summaryData) ? <div role="group" className="mb-4 flex flex-wrap gap-x-6 gap-y-2 border-b border-slate-200 pb-3" aria-label="Portfolio totals">
            {summaryData.map(item => <div key={item.label} className="flex items-baseline gap-2">
              <span className="text-xs text-slate-500">{item.label.replace("Total ", "")}</span>
              <span className="text-sm font-semibold tabular-nums text-slate-800">{item.value ?? "—"}</span>
            </div>)}
          </div> : null}
          <section aria-label="Portfolio filters">
            <div>
              <DashBoardFilterPane
                activeFilters={activeFilters}
                activeView={activeView}
                farmOptions={farmOptions}
                fieldOptions={fieldOptions}
                cropYearOptions={cropYearOptions}
                onFarmChange={handleFarmChange}
                onFieldChange={handleFieldChange}
                onCropYearChange={handleCropYearChange}
                onClearFilters={handleClearFilters}
                onViewChange={setActiveView}
                resultsMeta={
                  activeView === "map"
                    ? `${filteredMapFieldData.length} mapped fields available`
                    : `${filteredTableData.length} crop years in view`
                }
              />
            </div>
          </section>

          <div className="mt-4">
            {loadError ? <div role="alert" className="mb-4 text-red-700">
              {loadError} <button className="underline" onClick={() => setReloadKey((key) => key + 1)}>Retry dashboard</button>
            </div> : null}
            {activeView === "map" ? (
              <DashboardMapView
                fields={filteredMapFieldData}
                isLoading={isLoading}
                selectedFieldId={selectedFieldId}
                onSelectField={setSelectedFieldId}
                onOpenField={handleOpenSelectedField}
                onGoPreviousField={() => handleSelectRelativeField(-1)}
                onGoNextField={() => handleSelectRelativeField(1)}
                GOOGLE_APIKEY={config?.GOOGLE_APIKEY}
              />
            ) : (
              <DashboardTable
                canGenerateAnalysis={canWriteSelectedGrower}
                tableData={filteredTableData}
                fields={filteredFieldData}
                isLoading={isLoading}
              />
            )}
          </div>
        </div>
        )}
      </div>
    </Page>
  );
}
